import { eq } from "drizzle-orm";
import { Box } from "@mui/material";
import type React from "react";
import { useContext, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";

// @ts-ignore
import type { SearchFeedsResult } from "@storyboard/admin-client";
import ChannelDial from "@/components/Icons/ChannelDial";
import { db } from "@/db/db";
import { feed as feedTable } from "@/db/schema";
import { emptyQuery } from "@/models/emptyQuery.ts";
import { useReactiveRelationalQuery } from "@/models/hooks/useReactiveRelationalQuery.ts";
import { TrackingContext } from "@/models/TrackingStateProvider";
import { UxContext } from "@/models/UxStateProvider";
import { sanitizeHtmlForDisplay } from "@/utils/sanitizeHtmlForDisplay";

export type MyActiveFeed = {
  id: string;
  title: string;
  workspaceId: string;
  updatedAt: string;
  groupId: string | null;
  readOnly: boolean;
  latestActivity: string | unknown;
  latestAliasActivity: string | unknown;
  aliasCount?: number | unknown;
};

function getFeedRecord(feedId?: string) {
  const query = db.query.feed.findFirst({
    where: eq(feedTable.id, feedId),
  });
  return feedId ? query : (emptyQuery as unknown as typeof query);
}

export default function ChannelItem({
  active,
  activeFeeds,
  activeLinkIndex,
  channel,
  id,
  itemIndex,
  lastItem,
}: {
  active: boolean;
  activeFeeds: MyActiveFeed[];
  activeLinkIndex: number;
  channel: SearchFeedsResult;
  id: string;
  itemIndex: number;
  lastItem: boolean;
}) {
  const { ampli } = useContext(TrackingContext);
  const { setGlobalSearchOpen } = useContext(UxContext);
  const linkRef = useRef<HTMLAnchorElement | null>(null);
  const feedRecord = useReactiveRelationalQuery(getFeedRecord, [channel.feedId], "ChannelItem.getFeed");
  const url = feedRecord?.aliasMembershipId
    ? `/workspaces/${channel.workspaceId}/drivers/${feedRecord.aliasMembershipId}/channel/${channel.feedId}`
    : `/workspaces/${channel.workspaceId}/channels/${channel.feedId}`;
  const hasPermissions = !!activeFeeds?.find((feed: MyActiveFeed) => feed.id === channel.feedId)?.id;
  const disabledPermissionsLink = !hasPermissions && "disabled";
  const activeLink = active && "active";
  const lastItemLink = lastItem && "last-item";
  const linkClassName = `search-item ${disabledPermissionsLink} ${activeLink} ${lastItemLink}`;
  const navigate = useNavigate();

  const clickEvent = async (e?: React.MouseEvent<HTMLAnchorElement>) => {
    if (e) {
      e.preventDefault();
    }
    setGlobalSearchOpen(() => false);
    ampli.searchLinkToMessage({ workspaceId: channel.workspaceId });
    navigate(url);
  };

  /**
   * Keyboard enter event handler
   */
  useEffect(() => {
    if (active && activeLinkIndex === itemIndex && linkRef.current) {
      clickEvent().then(() => {
        navigate(url);
      });
    }
  }, [active, activeLinkIndex, active, itemIndex, clickEvent]);

  return (
    <Link ref={linkRef} id={id} to={url} className={linkClassName} onClick={(e) => clickEvent(e)}>
      <>
        <Box className="channel-icon">
          <ChannelDial />
        </Box>
        <Box
          dangerouslySetInnerHTML={{
            __html: sanitizeHtmlForDisplay(channel.title ?? ""),
          }}
        />
      </>
    </Link>
  );
}
